import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useLoadScript, Polygon, Marker, InfoWindow, Libraries } from '@react-google-maps/api';
import { Segment, Input, Button, Icon, Label, Message } from 'semantic-ui-react';
import { GeofencePointDto } from '../../../models/requests/geofence-point-dto';
import { ProgrammingLocationDto } from '../../../models/programming-location-dto';
import moment from 'moment';
import { formatEstimatedArrival, getTrackingStatusColor, getTrackingStatusLabel, normalizeTrackingCoordinates, shouldFallbackToBrazilOverview, shouldRecenterTrackingFocus, shouldResetTrackingFocusRef } from '../../../shared/utils/torre-tracking-utils';
import { formatProgrammingDateTime, getProgrammingTimelineStages } from '../../../utils/programming-timeline-utils';
import { getGoogleMapsApiKey } from '../../../config/googleMapsConfig';
import { appendGeofenceClickPoint, removeLastGeofencePoint, resolveGeofenceMapClick, shouldRecenterGeofenceMapViewport } from '../../../shared/utils/geofence-drawing-utils';


const libraries: Libraries = ["places"];
const DEFAULT_CENTER = { lat: -23.550520, lng: -46.633308 };
const DEFAULT_ZOOM = 5;

interface GoogleGeofenceMapProps {
    // Props originais para geofence
    readOnly?: boolean;
    initialPoints?: GeofencePointDto[];
    onPointsChange?: (points: GeofencePointDto[]) => void;
    
    // Props novas para tracking de programações
    mode?: 'geofence' | 'tracking';
    selectedProgramming?: ProgrammingLocationDto | null;
    selectedProgrammingId?: string | null;
    trackingLocations?: ProgrammingLocationDto[];
    onMarkerCountChange?: (count: number) => void;
    mapHeight?: string;
    showTrackingLegend?: boolean;
    onProgrammingSelect?: (programming: ProgrammingLocationDto) => void;
}

interface GoogleGeofenceMapInnerProps extends GoogleGeofenceMapProps {
    apiKey: string;
}

const GoogleGeofenceMapInner: React.FC<GoogleGeofenceMapInnerProps> = ({
                                                                 readOnly = false,
                                                                 initialPoints = [],
                                                                 onPointsChange,
                                                                 mode = 'geofence',
                                                                 selectedProgramming,
                                                                 selectedProgrammingId,
                                                                 trackingLocations = [],
                                                                 onMarkerCountChange,
                                                                 mapHeight,
                                                                 showTrackingLegend = true,
                                                                 onProgrammingSelect,
                                                                 apiKey
                                                             }) => {
    const [authError, setAuthError] = useState(false);
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: apiKey,
        libraries: libraries
    });

    useEffect(() => {
        const previousHandler = (window as typeof window & { gm_authFailure?: () => void }).gm_authFailure;
        (window as typeof window & { gm_authFailure?: () => void }).gm_authFailure = () => setAuthError(true);
        return () => {
            if (previousHandler) {
                (window as typeof window & { gm_authFailure?: () => void }).gm_authFailure = previousHandler;
            } else {
                delete (window as typeof window & { gm_authFailure?: () => void }).gm_authFailure;
            }
        };
    }, []);

    const [points, setPoints] = useState<GeofencePointDto[]>(initialPoints);
    const [isDrawing, setIsDrawing] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const currentPolygonRef = useRef<google.maps.Polygon | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const lastFocusedProgrammingIdRef = useRef<string | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [trackingInfoWindow, setTrackingInfoWindow] = useState<ProgrammingLocationDto | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [googleEtaText, setGoogleEtaText] = useState<string | null>(null);
    const [googleEtaLoading, setGoogleEtaLoading] = useState(false);
    const routeEtaCacheRef = useRef<Record<string, { text: string; expiresAt: number }>>({});

    const mapContainerStyle = useMemo(() => ({
        width: '100%',
        height: mapHeight || 'clamp(360px, 70vh, 600px)',
        borderRadius: '.28571429rem'
    }), [mapHeight]);

    const mapOptions = useMemo<google.maps.MapOptions>(() => {
        const options: google.maps.MapOptions = {
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true
        };
        if (isLoaded && window.google?.maps?.ControlPosition) {
            options.mapTypeControlOptions = { position: google.maps.ControlPosition.TOP_RIGHT };
            options.fullscreenControlOptions = { position: google.maps.ControlPosition.RIGHT_TOP };
        }
        return options;
    }, [isLoaded]);

    const clearCurrentPolygon = () => {
        if (currentPolygonRef.current) {
            currentPolygonRef.current.setMap(null);
            currentPolygonRef.current = null;
        }

        // Limpar marcadores existentes
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
    };

    const commitPoints = (next: GeofencePointDto[]) => {
        setPoints(next);
        onPointsChange?.(next);
    };

    const toggleDrawing = () => {
        if (readOnly || mode !== 'geofence') {
            return;
        }
        setIsDrawing(prev => !prev);
    };

    const handleGeofenceMapClick = (event: google.maps.MapMouseEvent) => {
        if (mode === 'tracking') {
            setTrackingInfoWindow(null);
            return;
        }

        const click = resolveGeofenceMapClick({
            mode,
            isDrawing,
            readOnly,
            latLng: event.latLng
        });
        if (!click) {
            return;
        }

        commitPoints(appendGeofenceClickPoint(points, click.lat, click.lng));
    };

    const handleUndoLastPoint = () => {
        if (readOnly || points.length === 0) {
            return;
        }
        commitPoints(removeLastGeofencePoint(points));
    };

    const handleClearPoints = () => {
        if (readOnly) {
            return;
        }
        commitPoints([]);
        clearCurrentPolygon();
    };

    const handleSearch = () => {
        if (mapRef.current && searchValue.trim()) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { address: searchValue },
                (results, status) => {
                    if (status === 'OK' && results && results.length > 0) {
                        const location = results[0].geometry.location;
                        mapRef.current?.panTo(location);
                        mapRef.current?.setZoom(15);
                        
                        setHasSearched(true);
                    }
                }
            );
        }
    };

    // ===== FUNÇÕES PARA TRACKING DE PROGRAMAÇÕES =====
    
    const getTrackingPosition = (location: ProgrammingLocationDto) => {
        const lat = location.currentLatitude ?? location.latitude;
        const lng = location.currentLongitude ?? location.longitude;
        return normalizeTrackingCoordinates(lat, lng);
    };

    const getMapFocusPosition = (location: ProgrammingLocationDto) => {
        return getTrackingPosition(location) ?? getDestinationPosition(location);
    };

    const getDestinationPosition = (location: ProgrammingLocationDto) => {
        if (location.destinationLatitude === undefined || location.destinationLongitude === undefined) return null;
        return { lat: Number(location.destinationLatitude), lng: Number(location.destinationLongitude) };
    };

    const getMapsRouteLink = (origin: { lat: number; lng: number } | null, destination: { lat: number; lng: number } | null) => {
        if (!destination) return null;
        if (!origin) {
            return `https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`;
        }
        return `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}&travelmode=driving`;
    };

    const resolveGoogleRouteEta = async (location: ProgrammingLocationDto): Promise<string | null> => {
        const origin = getTrackingPosition(location);
        const destination = getDestinationPosition(location);
        if (!origin || !destination || !window.google?.maps?.DirectionsService) return null;

        const rounded = (value: number) => value.toFixed(5);
        const cacheKey = `${location.id}-${rounded(origin.lat)}-${rounded(origin.lng)}-${rounded(destination.lat)}-${rounded(destination.lng)}`;
        const cached = routeEtaCacheRef.current[cacheKey];

        if (cached && cached.expiresAt > Date.now()) {
            return cached.text;
        }

        setGoogleEtaLoading(true);
        try {
            const directionsService = new window.google.maps.DirectionsService();
            const etaText = await new Promise<string | null>((resolve) => {
                directionsService.route(
                    {
                        origin,
                        destination,
                        travelMode: google.maps.TravelMode.DRIVING,
                        drivingOptions: {
                            departureTime: new Date(),
                            trafficModel: google.maps.TrafficModel.BEST_GUESS
                        }
                    },
                    (result, status) => {
                        if (status !== google.maps.DirectionsStatus.OK || !result?.routes?.length) {
                            resolve(null);
                            return;
                        }

                        const leg = result.routes[0].legs?.[0];
                        resolve(leg?.duration_in_traffic?.text || leg?.duration?.text || null);
                    }
                );
            });

            if (etaText) {
                routeEtaCacheRef.current[cacheKey] = {
                    text: etaText,
                    expiresAt: Date.now() + 5 * 60 * 1000
                };
            }

            return etaText;
        } finally {
            setGoogleEtaLoading(false);
        }
    };

    const createTrackingMarkerIcon = (status: string | number, isSelected: boolean = false) => {
        const color = getTrackingStatusColor(status);
        const size = isSelected ? 44 : 34;
        const border = isSelected ? 3 : 2;

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="${isSelected ? 30 : 28}" fill="${color}" stroke="#fff" stroke-width="${border}" />
                    <g fill="#fff">
                        <path d="M15 25h25l6 7h5c2.2 0 4 1.8 4 4v8h-4a6 6 0 0 1-12 0H25a6 6 0 0 1-12 0h-4v-9c0-5.5 4.5-10 10-10z"/>
                        <rect x="17" y="28" width="20" height="7" rx="1.5"/>
                        <rect x="39" y="29" width="6" height="6" rx="1"/>
                    </g>
                    <circle cx="20" cy="46" r="4" fill="${color}" stroke="#fff" stroke-width="2"/>
                    <circle cx="44" cy="46" r="4" fill="${color}" stroke="#fff" stroke-width="2"/>
                </svg>
            `)}`,
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2)
        };
    };

    useEffect(() => {
        setPoints(initialPoints);
    }, [initialPoints]);

    useEffect(() => {
        if (!mapRef.current || !isLoaded) {
            return;
        }
        mapRef.current.setOptions({
            draggableCursor: isDrawing && mode === 'geofence' && !readOnly ? 'crosshair' : undefined
        });
    }, [isDrawing, isLoaded, mode, readOnly]);

    // Centraliza o mapa nos pontos
    const centerMapOnPoints = (points: GeofencePointDto[]) => {
        if (!mapRef.current || points.length === 0) return;

        // Limpar marcadores existentes
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];

        // Limpar polígono existente
        if (currentPolygonRef.current) {
            currentPolygonRef.current.setMap(null);
            currentPolygonRef.current = null;
        }

        // Criar bounds
        const bounds = new google.maps.LatLngBounds();

        // Adicionar cada ponto ao bounds e criar marcadores mais visíveis
        points.forEach((point, index) => {
            const position = {
                lat: Number(point.latitude),
                lng: Number(point.longitude)
            };

            bounds.extend(position);

            // Criar marcador com ícone mais visível
            const marker = new google.maps.Marker({
                position: position,
                map: mapRef.current,
                title: `Ponto ${index + 1}`,
                label: {
                    text: `${index + 1}`,
                    color: '#FFFFFF',
                    fontWeight: 'bold'
                },
                // Usar marcador padrão vermelho para maior visibilidade
                icon: {
                    url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
                }
            });

            markersRef.current.push(marker);
        });

        // Se houver mais de um ponto, desenhar um polígono
        if (points.length > 1) {
            const polygonPath = points.map(point => ({
                lat: Number(point.latitude),
                lng: Number(point.longitude)
            }));

            const polygon = new google.maps.Polygon({
                paths: polygonPath,
                strokeColor: '#FF0000',
                strokeOpacity: 1.0,  // Máxima opacidade para maior visibilidade
                strokeWeight: 4,     // Linha mais grossa
                fillColor: '#FF0000',
                fillOpacity: 0.35,
                editable: !readOnly,
                zIndex: 1
            });

            polygon.setMap(mapRef.current);
            currentPolygonRef.current = polygon;
        }

        // Ajustar o zoom/centro do mapa
        if (points.length === 1) {
            mapRef.current.setCenter({
                lat: Number(points[0].latitude),
                lng: Number(points[0].longitude)
            });
            mapRef.current.setZoom(15);
        } else {
            mapRef.current.fitBounds(bounds);
            // Adicionar pequeno zoom out para garantir que todos os pontos sejam visíveis
            const listener = google.maps.event.addListener(mapRef.current, 'idle', function() {
                const currentZoom = mapRef.current?.getZoom();
                if (currentZoom && currentZoom > 16) {
                    mapRef.current?.setZoom(16);
                }
                google.maps.event.removeListener(listener);
            });
        }
    };

    // Efeito para renderizar pontos iniciais e centralizar
    useEffect(() => {
        if (!isLoaded || !mapRef.current) return;

        if (mode === 'geofence') {
            clearCurrentPolygon();
            if (initialPoints.length > 0
                && shouldRecenterGeofenceMapViewport({ hasUserMovedView: hasSearched, isDrawing })) {
                const bounds = new google.maps.LatLngBounds();
                initialPoints.forEach((point) => {
                    bounds.extend({ lat: Number(point.latitude), lng: Number(point.longitude) });
                });
                if (initialPoints.length === 1) {
                    mapRef.current.setCenter({
                        lat: Number(initialPoints[0].latitude),
                        lng: Number(initialPoints[0].longitude)
                    });
                    mapRef.current.setZoom(15);
                } else {
                    mapRef.current.fitBounds(bounds);
                }
            }
            return;
        }

        // Limpar polígonos e marcadores existentes
        clearCurrentPolygon();

        if (initialPoints.length === 0) return;

        // Criar marcadores para cada ponto, independentemente da quantidade
        initialPoints.forEach((point, index) => {
            const marker = new google.maps.Marker({
                position: {
                    lat: Number(point.latitude),
                    lng: Number(point.longitude)
                },
                map: mapRef.current,
                title: `Ponto ${index + 1}`,
                label: `${index + 1}`,
                animation: google.maps.Animation.DROP,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#FF0000',
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: '#FFFFFF'
                }
            });
            markersRef.current.push(marker);
        });

        // Se houver mais de um ponto, desenhar um polígono
        if (initialPoints.length > 1) {
            const polygonPath = initialPoints.map(point => ({
                lat: Number(point.latitude),
                lng: Number(point.longitude)
            }));

            const polygon = new google.maps.Polygon({
                paths: polygonPath,
                strokeColor: '#FF0000',
                strokeOpacity: 0.8,
                strokeWeight: 3,
                fillColor: '#FF0000',
                fillOpacity: 0.35,
                editable: !readOnly,
                zIndex: 1
            });

            polygon.setMap(mapRef.current);
            currentPolygonRef.current = polygon;

            // Adicionar listener para atualizar os pontos quando o polígono for editado (em modo edição)
            if (!readOnly) {
                google.maps.event.addListener(polygon, 'paths_changed', () => {
                    const path = polygon.getPath();
                    const newPoints: GeofencePointDto[] = [];

                    path.forEach((latLng, idx) => {
                        newPoints.push({
                            id: initialPoints[idx]?.id || '',
                            geofenceId: initialPoints[idx]?.geofenceId || '',
                            index: idx,
                            latitude: latLng.lat(),
                            longitude: latLng.lng(),
                            isActive: true,
                            createdAt: initialPoints[idx]?.createdAt || new Date()
                        });
                    });

                    // Atualizar marcadores para refletir a nova posição dos vértices
                    clearCurrentPolygon();
                    newPoints.forEach((point, idx) => {
                        const marker = new google.maps.Marker({
                            position: {
                                lat: Number(point.latitude),
                                lng: Number(point.longitude)
                            },
                            map: mapRef.current,
                            title: `Ponto ${idx + 1}`,
                            label: `${idx + 1}`,
                            icon: {
                                path: google.maps.SymbolPath.CIRCLE,
                                scale: 8,
                                fillColor: '#FF0000',
                                fillOpacity: 1,
                                strokeWeight: 2,
                                strokeColor: '#FFFFFF'
                            }
                        });
                        markersRef.current.push(marker);
                    });

                    setPoints(newPoints);
                    if (onPointsChange) {
                        onPointsChange(newPoints);
                    }
                });
            }
        }

        if (shouldRecenterGeofenceMapViewport({ hasUserMovedView: hasSearched, isDrawing })) {
            centerMapOnPoints(initialPoints);
        }

    }, [isLoaded, initialPoints, readOnly]);

    useEffect(() => {
        if (isLoaded && mapRef.current && initialPoints.length > 0
            && shouldRecenterGeofenceMapViewport({ hasUserMovedView: hasSearched, isDrawing })) {
            const timer = setTimeout(() => {
                centerMapOnPoints(initialPoints);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [isLoaded, hasSearched]);

    // ===== EFEITOS PARA TRACKING =====
    
    // Centraliza só quando o id selecionado muda; no mesmo id só atualiza InfoWindow (poll 30s).
    const selectionId = selectedProgrammingId ?? selectedProgramming?.id ?? null;

    useEffect(() => {
        if (mode !== 'tracking') return;

        if (shouldResetTrackingFocusRef(selectionId)) {
            lastFocusedProgrammingIdRef.current = null;
            return;
        }

        if (!selectedProgramming) {
            return;
        }

        const nextId = selectedProgramming.id;
        const shouldRecenter = shouldRecenterTrackingFocus(lastFocusedProgrammingIdRef.current, nextId);

        if (shouldRecenter && mapRef.current) {
            const focusPosition = getMapFocusPosition(selectedProgramming);
            if (focusPosition) {
                mapRef.current.setCenter(focusPosition);
                mapRef.current.setZoom(15);
                lastFocusedProgrammingIdRef.current = nextId;
            }
        }

        // Só abre InfoWindow com posição de tracking válida (evita crash do @react-google-maps/api).
        if (getTrackingPosition(selectedProgramming)) {
            setTrackingInfoWindow(selectedProgramming);
        }
    }, [mode, selectedProgramming, selectionId]);

    useEffect(() => {
        let cancelled = false;

        const loadGoogleEta = async () => {
            if (!trackingInfoWindow || mode !== 'tracking') return;
            setGoogleEtaText(null);

            const eta = await resolveGoogleRouteEta(trackingInfoWindow);
            if (!cancelled) {
                setGoogleEtaText(eta);
            }
        };

        loadGoogleEta();

        return () => {
            cancelled = true;
        };
    }, [trackingInfoWindow, mode]);

    // Efeito para ajustar zoom para mostrar todas as programações
    useEffect(() => {
        if (mode === 'tracking' && mapRef.current && trackingLocations.length > 0) {
            const validLocations = trackingLocations
                .map(loc => ({ loc, position: getTrackingPosition(loc) }))
                .filter(item => item.position !== null);

            if (validLocations.length === 0) {
                if (shouldFallbackToBrazilOverview(false, selectionId)) {
                    mapRef.current.setCenter({ lat: -15.7801, lng: -47.9292 });
                    mapRef.current.setZoom(4);
                }
                return;
            }

            if (!selectionId && validLocations.length > 1) {
                // Criar bounds para incluir todas as localizações
                const bounds = new google.maps.LatLngBounds();
                
                validLocations.forEach(item => {
                    bounds.extend(item.position!);
                });
                
                mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
            }

            // Callback para informar quantidade de marcadores
            if (onMarkerCountChange) {
                onMarkerCountChange(validLocations.length);
            }
        }
    }, [mode, trackingLocations, selectedProgramming, selectionId]);

    if (!isLoaded) {
        return <div>Carregando mapa...</div>;
    }

    if (loadError || authError) {
        const isLocalDev =
            typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        return (
            <Segment raised style={{ marginTop: '20px' }}>
                <Message negative>
                    <Message.Header>Erro ao carregar o Google Maps</Message.Header>
                    <p>
                        {loadError?.message ||
                            (isLocalDev
                                ? 'RefererNotAllowedMapError: a chave da API não autoriza este localhost (produção costuma funcionar).'
                                : 'Falha de autenticação da chave da API (mapa azul/cinza ou em branco).')}
                    </p>
                    <Message.List>
                        {isLocalDev && (
                            <Message.Item>
                                No Google Cloud Console → Credentials → restrições <strong>HTTP referrers</strong>,
                                adicione <code>http://localhost:3000/*</code>, <code>http://localhost:3001/*</code>,{' '}
                                <code>http://127.0.0.1:3000/*</code> e <code>http://127.0.0.1:3001/*</code> (sem remover
                                os de produção). Ou use uma chave de dev em <code>.env.local</code> (
                                <code>REACT_APP_GOOGLE_MAPS_API_KEY</code>). Ver <code>.env.example</code>.
                            </Message.Item>
                        )}
                        <Message.Item>Confirme que a <strong>Maps JavaScript API</strong> está ativa no Google Cloud.</Message.Item>
                        <Message.Item>Habilite também <strong>Places API</strong> e <strong>Directions API</strong>.</Message.Item>
                        <Message.Item>Confirme faturamento ativo e que os referrers de produção permanecem na lista.</Message.Item>
                    </Message.List>
                </Message>
            </Segment>
        );
    }

    return (
        <Segment raised style={{ padding: '0', marginTop: showTrackingLegend ? (mapHeight ? 0 : '20px') : 0 }}>
            {mode === 'tracking' && showTrackingLegend && (
                <div style={{ padding: '10px 15px', borderBottom: '1px solid #ececec', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Label size="small" color="blue">Aguardando</Label>
                    <Label size="small" color="yellow">Em operação</Label>
                    <Label size="small" color="red">Atrasadas</Label>
                    <Label size="small" color="green">Concluídas</Label>
                    <Label size="small" color="grey">Canceladas</Label>
                </div>
            )}
            {!readOnly && mode === 'geofence' && (
                <div style={{ padding: '15px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flex: '0 1 60%', maxWidth: '280px' }}>
                        <Input
                            placeholder="Pesquisar endereço..."
                            style={{ width: '100%' }}
                            value={searchValue}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                            action
                        >
                            <input />
                            <Button type="button" icon="search" onClick={handleSearch} />
                        </Input>
                    </div>
                    <Button
                        type="button"
                        icon
                        labelPosition="left"
                        onClick={toggleDrawing}
                        color={isDrawing ? 'red' : 'blue'}
                        data-testid="geofence-toggle-draw"
                    >
                        <Icon name={isDrawing ? 'stop' : 'pencil'} />
                        {isDrawing ? 'Parar Desenho' : 'Desenhar Polígono'}
                    </Button>
                    <Button.Group>
                        <Button
                            type="button"
                            icon="undo"
                            title="Remover último ponto"
                            disabled={points.length === 0}
                            onClick={handleUndoLastPoint}
                            data-testid="geofence-undo-point"
                        />
                        <Button
                            type="button"
                            icon="trash"
                            title="Limpar todos os pontos"
                            disabled={points.length === 0}
                            onClick={handleClearPoints}
                            data-testid="geofence-clear-points"
                        />
                    </Button.Group>
                    {isDrawing && (
                        <Label color="blue" data-testid="geofence-draw-hint">
                            Clique no mapa para marcar os vértices ({points.length} ponto{points.length === 1 ? '' : 's'})
                        </Label>
                    )}
                </div>
            )}
            <div style={{ position: 'relative' }}>
            {!readOnly && mode === 'geofence' && (
                <div
                    data-testid="geofence-draw-controls"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: 10,
                        transform: 'translateY(-50%)',
                        zIndex: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        padding: 6,
                        background: 'rgba(255,255,255,0.95)',
                        borderRadius: 4,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                    }}
                >
                    <Button
                        type="button"
                        icon
                        size="mini"
                        color={isDrawing ? 'red' : 'blue'}
                        title={isDrawing ? 'Parar desenho' : 'Desenhar polígono'}
                        onClick={toggleDrawing}
                    >
                        <Icon name={isDrawing ? 'stop' : 'pencil'} />
                    </Button>
                    <Button
                        type="button"
                        icon
                        size="mini"
                        title="Remover último ponto"
                        disabled={points.length === 0}
                        onClick={handleUndoLastPoint}
                    >
                        <Icon name="undo" />
                    </Button>
                    <Button
                        type="button"
                        icon
                        size="mini"
                        title="Limpar pontos"
                        disabled={points.length === 0}
                        onClick={handleClearPoints}
                    >
                        <Icon name="trash" />
                    </Button>
                </div>
            )}
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                onLoad={map => {
                    mapRef.current = map;
                    if (mode !== 'geofence' && initialPoints.length > 0) {
                        centerMapOnPoints(initialPoints);
                    }
                }}
                options={mapOptions}
                onClick={handleGeofenceMapClick}
            >
                {mode === 'geofence' && points.map((point, index) => (
                    <Marker
                        key={`geofence-point-${index}`}
                        position={{ lat: Number(point.latitude), lng: Number(point.longitude) }}
                        label={{ text: `${index + 1}`, color: '#FFFFFF', fontWeight: 'bold' }}
                        title={`Ponto ${index + 1}`}
                    />
                ))}
                {mode === 'geofence' && points.length > 1 && (
                    <Polygon
                        paths={points.map((point) => ({
                            lat: Number(point.latitude),
                            lng: Number(point.longitude)
                        }))}
                        options={{
                            strokeColor: '#FF0000',
                            strokeOpacity: 0.8,
                            strokeWeight: 3,
                            fillColor: '#FF0000',
                            fillOpacity: 0.35,
                            clickable: false,
                            zIndex: 1
                        }}
                    />
                )}
                {mode === 'tracking' && (
                    <>
                        {/* Marcadores de tracking para programações */}
                        {trackingLocations
                            .filter(location => getTrackingPosition(location) !== null)
                            .map((location, index) => {
                                const isSelected = selectedProgramming?.id === location.id;
                                const position = getTrackingPosition(location);
                                if (!position) return null;
                                
                                return (
                                    <Marker
                                        key={`tracking-${location.id}`}
                                        position={position}
                                        icon={createTrackingMarkerIcon(location.status, isSelected)}
                                        onClick={() => {
                                            setTrackingInfoWindow(location);
                                            onProgrammingSelect?.(location);
                                        }}
                                        zIndex={isSelected ? 1000 : 1}
                                    />
                                );
                            })
                        }
                        
                        {/* InfoWindow para tracking — position resolvido uma vez para evitar open sem coords */}
                        {(() => {
                            const infoPosition = trackingInfoWindow
                                ? getTrackingPosition(trackingInfoWindow)
                                : null;
                            if (!trackingInfoWindow || !infoPosition) return null;
                            return (
                            <InfoWindow
                                position={infoPosition}
                                onCloseClick={() => {
                                    setTrackingInfoWindow(null);
                                    setGoogleEtaText(null);
                                    setGoogleEtaLoading(false);
                                }}
                            >
                                <div style={{ minWidth: 260, maxWidth: 320, fontFamily: 'Arial, sans-serif' }}>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                        {trackingInfoWindow.mainVehiclePlateName || 'Sem placa'}
                                    </h4>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Motorista:</strong> {trackingInfoWindow.driverName || '-'}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Status:</strong> {getTrackingStatusLabel(trackingInfoWindow.status)}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Origem:</strong> {trackingInfoWindow.originReferenceName || '-'}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Destino:</strong> {trackingInfoWindow.destinationReferenceName || '-'}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Produto / Volume:</strong> {trackingInfoWindow.productName || '-'} / {trackingInfoWindow.volume ?? '-'}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>ETA (Google):</strong> {googleEtaLoading ? 'Calculando...' : (googleEtaText || '-')}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>ETA (planejada):</strong> {formatEstimatedArrival(trackingInfoWindow.expectedDestinationArrivalAt ?? trackingInfoWindow.expectedArrivalTime)}
                                    </p>
                                    {getProgrammingTimelineStages(trackingInfoWindow).map(stage => (
                                        <p key={stage.key} style={{ margin: '4px 0', fontSize: '0.92em' }}>
                                            <strong>{stage.label}:</strong>{' '}
                                            prev. {formatProgrammingDateTime(stage.expected)} · real {formatProgrammingDateTime(stage.actual)}
                                        </p>
                                    ))}
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Última atualização:</strong> {trackingInfoWindow.lastLocationUpdate ? moment(trackingInfoWindow.lastLocationUpdate).format('DD/MM/YYYY HH:mm') : '-'}
                                    </p>
                                    <p style={{ margin: '4px 0' }}>
                                        <strong>Endereço:</strong> {trackingInfoWindow.currentAddressInfo || trackingInfoWindow.addressInfo || '-'}
                                    </p>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                                        <a
                                            href={`/backoffice/programming/edit/${trackingInfoWindow.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ fontWeight: 600 }}
                                        >
                                            Abrir programação
                                        </a>
                                        {getMapsRouteLink(getTrackingPosition(trackingInfoWindow), getDestinationPosition(trackingInfoWindow)) && (
                                            <a
                                                href={getMapsRouteLink(getTrackingPosition(trackingInfoWindow), getDestinationPosition(trackingInfoWindow))!}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ fontWeight: 600 }}
                                            >
                                                Traçar rota
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                            );
                        })()}
                    </>
                )}
            </GoogleMap>
            </div>
        </Segment>
    );
};

const GoogleGeofenceMap: React.FC<GoogleGeofenceMapProps> = (props) => {
    const apiKey = getGoogleMapsApiKey();

    if (!apiKey) {
        return (
            <Segment raised style={{ marginTop: props.mapHeight ? 0 : '20px' }}>
                <Message warning>
                    <Message.Header>Google Maps não configurado</Message.Header>
                    <p>
                        Defina a chave em <code>src/config/googleMapsConfig.ts</code> ou em{' '}
                        <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> no <code>.env.local</code>.
                    </p>
                </Message>
            </Segment>
        );
    }

    return <GoogleGeofenceMapInner {...props} apiKey={apiKey} />;
};

export default GoogleGeofenceMap;